import { useAppDisplayModeContext } from '@archipro-design/aria';
import { Dialog } from '@archipro-design/aria';
import { useStyles } from '@archipro-design/aria';
import { useEffect, useState, useRef } from 'react';
import { useFetcher, useNavigate } from '@remix-run/react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { SubmitHandler, FieldValues } from 'react-hook-form';
import { DesignBoardDrawerHeader } from '@modules/design-board/component/right-side-drawer/DesignBoardRIghtSideDrawerHeader';
import { DesignBoardDrawerFooter } from '@modules/design-board/component/right-side-drawer/DesignBoardRightSideDrawerFooter';
import NewDesignBoardDrawerContent from '@modules/design-board/component/right-side-drawer/NewDesignBoardBody';
import { useDesignBoardPageState } from '@modules/design-board/component/context/DesignBoardContext';
import * as S from '@modules/design-board/component/right-side-drawer/RightSideNewBoarderDrawer.style';
import { DeleteDesignBoardDialog } from '@modules/design-board/component/right-side-drawer/DeleteDesignBoardDialog';
import type { NewBoardFormShape } from '@modules/design-board/validation/DesignBoardDetailPageSchemas';
import { NewBoardValidator } from '@modules/design-board/validation/DesignBoardDetailPageSchemas';
import { useToaster } from '~/modules/root/context/ToasterContext';
import { useSaveToDesignBoardContext } from '~/modules/design-board/component/context';
import type {
    CreateOrUpdateInspirationMutation,
    RemoveInspirationMutation,
} from 'generated/graphql';
import { REMOVE_BUILD_HUB_INSPIRATION_API_ROUTE } from '~/routes/remix-api.app.build-hub-inspiration-remove';
import { CREATE_BUILD_HUB_INSPIRATION_API_ROUTE } from '~/routes/remix-api.app.build-hub-inspiration-create';

const DeleteDesignBoardConfirmation = (props: {
    open: boolean;
    onCancel: () => void;
    onDelete: () => void;
    loading: boolean;
}) => {
    const { css } = useStyles();
    return (
        <Dialog
            open={props.open}
            onCancel={props.onCancel}
            content={
                <DeleteDesignBoardDialog
                    onDelete={props.onDelete}
                    onCancel={props.onCancel}
                    loading={props.loading}
                />
            }
            className={css(S.DeleteDialog)}
            footer={null}
            header={null}
        />
    );
};

const MoodBoardDrawerEditMainContent = () => {
    const { dispatch, state } = useDesignBoardPageState();
    const navigate = useNavigate();
    const { toast } = useToaster();
    const { css } = useStyles();
    const { mobile } = useAppDisplayModeContext();

    const [isEditBoardLoading, setIsEditBoardLoading] = useState(false);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const saveToBoardContext = useSaveToDesignBoardContext();

    // fetchers
    const updateFetcher = useFetcher<CreateOrUpdateInspirationMutation>();
    const deleteFetcher = useFetcher<RemoveInspirationMutation>();

    //refs
    const deletedBoardUrlSegmentRef = useRef<string | null>(null);
    const hasUpdated = useRef(false);
    const editingState = state.editMoodBoardInput;
    const creatingState = state.createMoodBoardInput;
    const isEditing = editingState !== undefined;

    const {
        control,
        formState: { errors },
        handleSubmit,
        reset,
    } = useForm<NewBoardFormShape>({
        resolver: zodResolver(NewBoardValidator),
        mode: 'onBlur',
        ...(editingState && {
            defaultValues: editingState,
        }),
    });

    const onSubmit: SubmitHandler<FieldValues> = (data) => {
        setIsEditBoardLoading(true);

        if (editingState) {
            hasUpdated.current = false;
            updateFetcher.submit(
                {
                    inspirationGroupId: editingState.id.toString(),
                    id: editingState.buildHubId.toString(),
                    title: data.title,
                    description: data.description,
                    isPrivate: data.isPrivate ? '1' : '0',
                },
                {
                    method: 'post',
                    action: CREATE_BUILD_HUB_INSPIRATION_API_ROUTE,
                }
            );
        } else if (creatingState) {
            updateFetcher.submit(
                {
                    id: creatingState.buildHubId.toString(),
                    title: data.title,
                    description: data.description,
                    isPrivate: data.isPrivate ? '1' : '0',
                },
                {
                    method: 'post',
                    action: CREATE_BUILD_HUB_INSPIRATION_API_ROUTE,
                }
            );
        }
    };

    const onDelete = () => {
        if (editingState) {
            deletedBoardUrlSegmentRef.current = editingState.buildHubUrl;
            deleteFetcher.submit(
                {
                    inspirationGroupId: editingState.id.toString(),
                    id: editingState.buildHubId.toString(),
                },
                {
                    method: 'post',
                    action: REMOVE_BUILD_HUB_INSPIRATION_API_ROUTE,
                }
            );
        }
    };

    // board deleted response - redirect back to project boards listing page
    useEffect(() => {
        const data = deleteFetcher.data?.projectInspirationGroupRemove;
        const isSuccess =
            deleteFetcher.state !== 'submitting' &&
            data?.__typename === 'InspirationGroup';

        if (isSuccess) {
            setIsDialogOpen(false);
            dispatch({ type: 'CloseDrawer' });
            reset();

            const urlSegment = deletedBoardUrlSegmentRef.current;
            if (urlSegment) {
                navigate(`/app/project/${urlSegment}/design-boards`, {
                    replace: true,
                });
                deletedBoardUrlSegmentRef.current = null;
            }
        }
    }, [deleteFetcher.state, deleteFetcher.data, dispatch, reset, navigate]);

    // board updated response
    useEffect(() => {
        const data = updateFetcher.data?.projectInspirationGroupUpsert;
        const isSuccess =
            updateFetcher.state === 'idle' &&
            data?.__typename === 'InspirationGroup' &&
            !hasUpdated.current;

        if (isSuccess) {
            hasUpdated.current = true;
            toast('Mood board has been updated.', { type: 'success' });
            dispatch({ type: 'CloseDrawer' });
            reset();
        }
    }, [
        updateFetcher.state,
        updateFetcher.data,
        saveToBoardContext,
        toast,
        dispatch,
        reset,
    ]);

    const onClose = (_openValue: boolean = false) => {
        dispatch({
            type: 'CloseDrawer',
        });
        reset();
    };

    const modalClose = () => {
        setIsDialogOpen(false);
    };

    return (
        <form
            id="design-board-create-and-edit-form"
            className={css(S.FormClass)}
            onSubmit={handleSubmit(onSubmit)}
        >
            <DesignBoardDrawerHeader
                {...{ onClose }}
                headerTitle={
                    !isEditing
                        ? mobile
                            ? 'Create board'
                            : 'Create a board'
                        : 'Edit board'
                }
            />

            <NewDesignBoardDrawerContent control={control} errors={errors} />

            <DesignBoardDrawerFooter
                loading={isEditBoardLoading}
                {...(editingState
                    ? {
                          submitButtonLabel: 'SAVE',
                          buttonSecondary: {
                              onClick: () => setIsDialogOpen(true),
                              label: 'DELETE BOARD',
                          },
                      }
                    : {
                          submitButtonLabel: 'CREATE',
                      })}
            />
            <DeleteDesignBoardConfirmation
                open={isDialogOpen}
                onCancel={modalClose}
                onDelete={onDelete}
                loading={deleteFetcher.state !== 'idle'}
            />
        </form>
    );
};

export default MoodBoardDrawerEditMainContent;
